import React from 'react';

import { css } from '@emotion/react';
import { CssVars, Type } from '@robinhood/design';
import { useDebounce } from '@x1/ui/react/hooks';

import { AddressInput } from 'src/clients/Network/gql/types/address';
import GoogleMapsClient, { GooglePlaceType, PlaceResult } from 'src/clients/external/GoogleMapsClient';
import TextInput from 'src/components/creditApplication/TextInput';
import { ToAddressType } from 'src/utils/address';

const MAX_SUGGESTIONS = 5;

type AddressComponent = {
  longText: string | null;
  shortText: string | null;
  types: string[];
};

export const parseAddressComponents = (components: AddressComponent[]): AddressInput => {
  const addressMap = {
    street_number: '',
    route: '',
    subpremise: '',
    sublocality_level_1: '',
    locality: '',
    administrative_area_level_1: '',
    postal_code: '',
  };

  // First pass: collect all components
  components.forEach((component) => {
    const matchingType = component.types.find((type) => type in addressMap);
    if (matchingType) {
      if (matchingType === 'administrative_area_level_1') {
        addressMap[matchingType] = component.shortText || '';
      } else {
        addressMap[matchingType as keyof typeof addressMap] = component.longText || '';
      }
    }
  });

  // Second pass: construct the address
  const address = {
    street: [addressMap.street_number, addressMap.route].join(' '),
    unit: addressMap.subpremise || undefined,
    city: addressMap.sublocality_level_1 || addressMap.locality,
    state: addressMap.administrative_area_level_1,
    zip: addressMap.postal_code,
  };
  return ToAddressType(address);
};

type AddressAutocompleteInputProps = {
  onSelect: (address: AddressInput | undefined) => void;
  placeholder?: string;
  autoCompleteTypes?: Array<GooglePlaceType>;
  value?: string;
  onChange?: (value: string) => void;
  regionCodes?: Array<string>;
};

const AddressAutocompleteInput = ({
  onSelect,
  placeholder = 'Search for an address',
  autoCompleteTypes = ['street_address'],
  regionCodes = ['US'],
  value: controlledValue,
  onChange: controlledOnChange,
}: AddressAutocompleteInputProps) => {
  const [internalValue, setInternalValue] = React.useState('');
  const [suggestions, setSuggestions] = React.useState<PlaceResult[]>([]);
  const [isSelectingRef] = React.useState(() => ({ current: false }));
  const [latestFetchAtRef] = React.useState(() => ({ current: 0 }));
  const [isLoading, setIsLoading] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(false);

  // Use controlled value if provided, otherwise use internal state
  const value = controlledValue !== undefined ? controlledValue : internalValue;
  const setValue = controlledOnChange !== undefined ? controlledOnChange : setInternalValue;

  const debouncedValue = useDebounce(value, 300);

  const fetchSuggestions = React.useCallback(
    async (text: string) => {
      if (!text.trim()) {
        setSuggestions([]);
        setIsLoading(false);
        return;
      }

      const fetchAt = new Date().valueOf();
      latestFetchAtRef.current = fetchAt;
      setIsLoading(true);

      try {
        const response = await GoogleMapsClient.getPlacesAutocomplete({
          input: text,
          types: autoCompleteTypes,
          regionCodes,
        });

        // Only set results for the most recent search
        if (response?.predictions && latestFetchAtRef.current === fetchAt) {
          setSuggestions(response.predictions);
        } else {
          setSuggestions([]);
        }
      } catch (error) {
        console.error('Error fetching address suggestions:', error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [autoCompleteTypes],
  );

  const handleSelectAddress = React.useCallback(
    (place: PlaceResult) => async () => {
      isSelectingRef.current = true;
      setIsLoading(true);

      try {
        const response = await GoogleMapsClient.getPlaceDetails({
          place_id: place.place_id,
          fields: ['addressComponents'],
        });

        if (response?.result?.addressComponents) {
          const parsedAddress = parseAddressComponents(response.result.addressComponents);
          onSelect?.(parsedAddress);
        }

        setValue(place.description);
        setSuggestions([]);
        setIsVisible(false);
      } catch (error) {
        console.error('Error getting place details:', error);
      } finally {
        setIsLoading(false);
        isSelectingRef.current = false;
      }
    },
    [onSelect, setValue],
  );

  const handleInputChange = React.useCallback(
    ({ value: newValue }: { event: React.ChangeEvent<HTMLInputElement>; value: string }) => {
      isSelectingRef.current = false;
      setValue(newValue);
      if (!newValue.trim()) {
        onSelect?.(undefined);
        setSuggestions([]);
      }
    },
    [onSelect, setValue],
  );

  const handleInputFocus = React.useCallback(() => {
    setIsVisible(true);
  }, []);

  const handleInputBlur = React.useCallback(() => {
    // Delay hiding to allow for click on suggestions
    setTimeout(() => {
      setIsVisible(false);
    }, 200);
  }, []);

  // Fetch suggestions when debounced value changes
  React.useEffect(() => {
    if (Boolean(debouncedValue) && !isSelectingRef.current && isVisible) {
      fetchSuggestions(debouncedValue);
    }
  }, [debouncedValue, fetchSuggestions, isVisible]);

  const showSuggestions = isVisible && suggestions.length > 0;

  return (
    <div css={containerStyle}>
      <TextInput
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
      />
      {showSuggestions && (
        <div css={suggestionsContainerStyle}>
          {suggestions.slice(0, MAX_SUGGESTIONS).map((suggestion, index) => (
            <div
              key={suggestion.place_id}
              css={[suggestionItemStyle, index > 0 && suggestionBorderStyle]}
              onClick={handleSelectAddress(suggestion)}
              onMouseDown={(e) => e.preventDefault()} // Prevent blur when clicking
            >
              <Type.Text size='M'>{suggestion.description}</Type.Text>
            </div>
          ))}
        </div>
      )}
      {isLoading && (
        <div css={loadingStyle}>
          <Type.Text size='M'>Loading...</Type.Text>
        </div>
      )}
    </div>
  );
};

const containerStyle = css`
  position: relative;
  width: 100%;
`;

const suggestionsContainerStyle = css`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${CssVars.neutral.bg2};
  border: 1px solid ${CssVars.neutral.bg3};
  border-radius: 12px;
  margin-top: 8px;
`;

const suggestionItemStyle = css`
  padding: 16px 24px;
  cursor: pointer;
  transition: background-color 0.15s ease-in-out;

  &:hover {
    background-color: ${CssVars.neutral.bg3};
  }

  &:first-of-type {
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
  }

  &:last-of-type {
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
  }
`;

const suggestionBorderStyle = css`
  border-top: 1px solid ${CssVars.neutral.bg3};
`;

const loadingStyle = css`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 999;
  background: ${CssVars.neutral.bg2};
  border: 1px solid ${CssVars.neutral.bg3};
  border-radius: 12px;
  padding: 16px 24px;
  margin-top: 8px;
`;

export default AddressAutocompleteInput;
